import Vue from 'vue'
import AudioVisual from 'vue-audio-visual'
import Router from 'vue-router'

import 'vuetify/dist/vuetify.min.css'

// Sentry integration
import * as Sentry from '@sentry/vue'
import { browserTracingIntegration } from '@sentry/vue'

import './filters'
import store from './store'
import { BrowserCompatible } from './store/class/helper'
import App from './App.vue'
import i18n from './i18n'
import router from './router'
import Timer from './timer'
import Logger from './logger'
import HelpTooltips from './components/help'
import vuetify from './plugins/vuetify'
import GA from './plugins/ga'
import VueTelInputVuetify from 'vue-tel-input-vuetify/lib'
import VueDragResize from 'vue-drag-resize'
import VCountryRegionSelect, { VCountrySelect, VRegionSelect } from '@timbouc/vuetify-country-region-input'
import CountryFlag from 'vue-country-flag'
import EmbeddedLocalStorage from '@/api/embedded'
import Session from '@/api/session'
import SessionClient from '@/api/client'
import pkg from '../package.json'

Vue.component('vue-country-flag', CountryFlag)
Vue.component('vue-drag-resize', VueDragResize)
Vue.use(VCountryRegionSelect, { components: { VCountrySelect, VRegionSelect } })

if (import.meta.env.NODE_ENV !== 'test') {
  Vue.use(Router)
}

if (import.meta.env.NODE_ENV === 'production') {
  const apiUrl = new URL(`https://${import.meta.env.TLD}`)
  const callPanelUrl = new URL(`https://callpanel.${apiUrl.host}`)
  Sentry.init({
    Vue,
    dsn: 'https://f0629b77bac34978a8b6d5257783e3e8@sentry.clearlyip.com/21',
    release: pkg.version,
    integrations: [
      browserTracingIntegration({
        tracePropagationTargets: [apiUrl.host, callPanelUrl.host]
      })
    ],
    sampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0.0,
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.0,
    profilesSampleRate: 0.0,
    // call Vue's origin logError to dev console
    logErrors: true,
    ignoreErrors: [
      /feature named `webCompat` was not found/
    ]
  })
}

Vue.config.productionTip = false
Vue.prototype.$APPVERSION = pkg.version

Vue.use(Timer)
Vue.use(Logger)
Vue.use(AudioVisual)
Vue.use(HelpTooltips)
Vue.use(GA) // google analytics
Vue.use(VueTelInputVuetify, {
  vuetify,
  placeholder: '',
  validCharactersOnly: true
})

i18n.app.locale = i18n.getLocale(window.navigator.language || window.navigator.browserLanguage)

window.AudioContext = window.AudioContext ||
  window.webkitAudioContext ||
  window.mozAudioContext ||
  window.msAudioContext

window.requestAnimationFrame = window.requestAnimationFrame ||
  window.webkitRequestAnimationFrame ||
  window.mozRequestAnimationFrame ||
  window.msRequestAnimationFrame

window.cancelAnimationFrame = window.cancelAnimationFrame || window.mozCancelAnimationFrame

/**
 * Router navigation guard
 */
router.beforeEach(async (to, _, next) => {
  // set embedded flag in localStorage
  const urlParams = new URLSearchParams(window.location.search)
  const embedded = urlParams.get('embedded') === 'true'
  if (embedded) {
    EmbeddedLocalStorage.set(embedded)
  }

  // allows public routes
  const isPublic = ['oauth2', 'embedded_login'].includes(to.name)
  if (isPublic) return next()

  // check browser compatibility based on browser version etc
  if (!BrowserCompatible(window.navigator.userAgent, embedded)) {
    if (to.name === 'unsupported') {
      return next()
    } else {
      return next({ name: 'unsupported' })
    }
  }

  // check if route is valid
  if (to.matched.length === 0) {
    return next({ path: '/' })
  }

  if (!Session.isValid()) {
    try {
      await SessionClient.refreshOrLogin()
      if (Session.isValid()) {
        await store.dispatch('setPendingInitAfterRefresh', true)
        await store.dispatch('authSet')
      }
    } catch (err) {
      await SessionClient.loginRedirect()
      return
    }
  }

  return next()
})

new Vue({
  router,
  store,
  i18n: i18n.app,
  vuetify,
  render: h => h(App)
}).$mount('#app')
